import PropTypes from 'prop-types';
import React, { PureComponent, Fragment } from 'react';
import { Query, Mutation, withApollo } from 'react-apollo';
import { connect } from 'react-redux';
import { replace } from 'connected-react-router';
import { FormattedMessage } from 'react-intl-next';
import { getLocation } from '@confluence-classic/confluence-urls';

import SectionMessage from '@atlaskit/section-message/section-message';
import Button from '@atlaskit/button/new';
import { token } from '@atlaskit/tokens';

import { ErrorDisplay, isUnauthorizedError } from '@confluence/error-boundary';
import {
	ReactionsProvider,
	ReactionsContext,
	PageCommentsCountContext,
} from '@confluence/comment-context';
import {
	ReloadType,
	QuickReloadSubscriber,
} from '@confluence/quick-reload/entry-points/subscription';
import { CommentPlaceholder } from '@confluence/comment-simple/entry-points/CommentPlaceholder';
import { markErrorAsHandled } from '@confluence/graphql';
import { AutoReloadPageComments } from '@confluence/page-comments';
import { DEFAULT_LIMIT } from '@confluence/page-comments-queries';
import {
	CommentsSectionQuery,
	CommentsSectionWithoutReactionsQuery,
} from '@confluence/page-comments-queries/entry-points/CommentsSectionQuery.graphql';
import {
	CommentsSectionGetSingleCommentQuery,
	CommentsSectionGetSingleCommentWithoutReactionsQuery,
} from '@confluence/page-comments-queries/entry-points/CommentsSectionGetSingleCommentQuery.graphql';

import { CommentsSectionFabricComponent } from './CommentsSectionFabricComponent';
import { CommentsSectionFabricQuerySaveComment } from './queries/CommentsSectionFabricSaveCommentQuery.experimentalgraphql.js';
import { CommentsSectionQueryDeleteComment } from './queries/CommentsSectionDeleteCommentQueryExperimental.experimentalgraphql.js';
import { i18n } from './commentsI18n.js';

/* Generic View comment error display*/
/* eslint-disable-next-line react/prop-types */
const ViewCommentError = ({ onLinkClick }) => (
	<SectionMessage testId="view-comment-error" appearance="warning">
		<FormattedMessage {...i18n.viewCommentErrorMessage} tagName="div" />
		{/* eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop */}
		<div style={{ marginTop: token('space.100', '8px') }}>
			<Button appearance="primary" testId="view-comment-error-refresh-link" onClick={onLinkClick}>
				<FormattedMessage {...i18n.viewCommentErrorRefresh} />
			</Button>
		</div>
	</SectionMessage>
);

const isParentRestrictedError = (error) => {
	return error?.message?.includes?.(
		'com.atlassian.confluence.api.service.exceptions.PermissionException: Parent page view is restricted',
	);
};

const isExpectedError = (error) =>
	error.graphQLErrors.every((gqlError) => isParentRestrictedError(gqlError));

function mapStateToProps(state, ownProps) {
	const { commentsData } = ownProps;

	const commentsIds = commentsData.map((comment) => comment.id);

	const location = getLocation(state);

	const { focusedCommentId, replyToComment, showCommentArea, editComment, commentId } =
		location.query;

	const openEditCommentId = (editComment === 'true' && commentId) || '';

	/* Check if its a valid Reply for page comments*/
	const isReplyCommentFound = commentsIds.indexOf(replyToComment) !== -1;

	const replyToCommentId = isReplyCommentFound ? replyToComment : '';

	const focusOnCommentId = focusedCommentId || replyToCommentId;

	const isEditCommentFound = !!openEditCommentId && commentsIds.indexOf(openEditCommentId) !== -1;

	//Edit Comment Object
	const commentIdObject = commentsData.find((comment) => comment.id === openEditCommentId);

	const isCommentEditable = commentIdObject?.permissions?.isEditable ?? false;

	//check if the comment thats edited has update operation
	const isEditCommentAllowed = !!(isEditCommentFound && isCommentEditable);

	return {
		focusOnCommentId,
		replyToCommentId,
		shouldOpenAddComment: showCommentArea === 'true',
		openEditCommentId,
		isEditCommentFound,
		commentsData,
		commentsIds,
		location,
		isEditCommentAllowed,
	};
}

function mapDispatchToProps(dispatch) {
	return {
		replace: (location) => dispatch(replace(location)),
	};
}

const CommentsSectionRedux = connect(
	mapStateToProps,
	mapDispatchToProps,
)(CommentsSectionFabricComponent);

let editedCommentsIds = [];

const CommentsSectionFabric = withApollo(
	class CommentsSectionInner extends PureComponent {
		static propTypes = {
			/**
			 * The content ID to fetch comments from.
			 */
			contentId: PropTypes.string.isRequired,
			client: PropTypes.object,
			isPageCommentsSortingExperimentEnabled: PropTypes.bool.isRequired,
			pageCommentsSortingExperimentCohort: PropTypes.oneOf([
				'experimentOldestButtonTop',
				'experimentNewestButtonTop',
			]),
			handlePageCommentsRender: PropTypes.func,
			isAutoReloadPageCommentsEnabled: PropTypes.bool,
			inlineCommentsCount: PropTypes.number,
		};

		state = {
			isAutoScrollingEnabled: true,
		};

		_loadedCursor = {};

		_getCommentVersion = (comment) => ({
			id: comment?.node?.id,
			by: comment?.node?.version?.by,
			when: comment?.node?.version?.when,
			number: comment?.node?.version?.number,
		});

		_buildUserInfo = (user) => ({
			displayName: user?.displayName,
			avatarUrl:
				user?.photos.length && user?.photos?.find((photoData) => photoData.isPrimary).value,
			accountId: user?.id,
		});
		_getADF(adf) {
			if (adf) {
				try {
					return JSON.parse(adf);
				} catch (e) {
					// eslint-disable-next-line
					console.error('Invalid adf document', e);
				}
			}

			return null;
		}

		// WARN: When CommentsSection is not remounted upon `contentId` update (the way it should work)
		// there is a chance to see stale comments from other pages. This helps to prevent stale data
		// from displaying when transitioning from page to page.
		// Do not allow to show comments when `contentId` in comment data doesn't match `contentId`
		// passed through props.
		_canShowCommentsWhenLoading(data, loading) {
			const commentContentId = data?.content?.nodes[0]?.id;

			return loading ? commentContentId === this.props.contentId : true;
		}

		/* On refetching comments using QuickReload reset _loadedCursor so comments > 100 load*/
		async _refetchComments(refetch) {
			this.setState({ isAutoScrollingEnabled: false }, async () => {
				this._loadedCursor = {};
				await refetch();
			});
		}

		enableScrolling() {
			this.setState({ isAutoScrollingEnabled: true });
		}

		_buildCommentsData(data) {
			const edges = data?.comments?.edges;

			if (!edges || edges.length === 0) {
				return [];
			}

			return edges.map((edge) => {
				const ancestors = edge?.node?.ancestors ?? [];
				const depthLevel = ancestors?.length ?? 0;
				const parentId = edge?.node?.parentId ?? null;
				const adf = this._getADF(edge?.node?.body?.value);
				const version = this._getCommentVersion(edge);

				const author = edge?.node?.author ?? {};
				let createdBy;
				if (author.type === 'anonymous') {
					createdBy = {
						isAnonymous: true,
						avatarUrl: author.profilePicture.path,
					};
				} else {
					createdBy = {
						isAnonymous: false,
						displayName: author.displayName,
						avatarUrl: author.profilePicture.path,
						accountId: author.accountId,
						permissionType: author.permissionType,
					};
				}

				const createdByAuthor = Boolean(
					createdBy.accountId &&
						data?.content?.nodes[0]?.history?.createdBy?.accountId &&
						createdBy.accountId === data?.content?.nodes[0]?.history?.createdBy?.accountId,
				);
				const id = edge?.node?.id ?? '';
				const linkWebui = edge?.node?.links?.webui ?? '';
				const permissions = {
					isEditable: edge?.node?.permissions?.isEditable ?? false,
					isRemovable: edge?.node?.permissions?.isRemovable ?? false,
				};
				const createdFriendly = edge?.node?.version?.friendlyWhen ?? '';
				const contentStatus = edge?.node?.contentStatus;

				const reactionsSummary = edge?.node?.reactionsSummary;

				return {
					adf,
					ancestors,
					depthLevel,
					parentId,
					version,
					createdBy,
					createdByAuthor,
					id,
					linkWebui,
					operations: [],
					permissions,
					createdFriendly,
					contentStatus,
					reactionsSummary,
				};
			});
		}

		_saveComment = (contentId, contentType, experimentalFabricSaveComment) => (props) => {
			const {
				contentId: passedContentId,
				commentId = '', //undefined when adding new comment
				adf,
				requestType,
				version,
			} = props;

			return experimentalFabricSaveComment({
				variables: {
					params: {
						location: 'footer',
						contentId: passedContentId || contentId,
						contentType,
						adfContent: JSON.stringify(adf),
						commentId,
						requestType,
						version,
					},
				},
			});
		};

		_deleteComment = (contentId, experimentalDeleteComment) => (contentId) =>
			experimentalDeleteComment({
				variables: {
					contentId,
				},
			});

		/**
		 * to perform incremental loading of comment
		 * @param {Function} fetchMore - from GraphQL to fetch a new set of comment
		 *        { Object } variables - object with parameters to fetch new comment
		 */
		_fetchMore(fetchMore, variables) {
			return fetchMore({
				variables,
				updateQuery: (prevData, { fetchMoreResult: newData }) => {
					if (!newData) return prevData;

					// new cache
					return Object.assign({}, prevData, {
						comments: {
							totalCount: newData.comments.totalCount,
							pageInfo: {
								...newData.comments.pageInfo,
								__typename: newData.comments.pageInfo.__typename,
							},
							edges: [...prevData.comments.edges, ...newData.comments.edges],
							__typename: newData.comments.__typename,
						},
						content: {
							nodes: [...newData.content.nodes],
							__typename: newData.content.__typename,
						},
					});
				},
			});
		}
		/**
		 * Get called whenever a comment is added, deleted or edited to
		 * to update the cache with new comment data
		 *  @param { Object } refetchOptions consists of
		 *                      commentId (String): the id of new comment to be fetched;
		 *                      mode (String): either `addComment`, `replyComment` or `editComment` or `deleteComment`
		 *         { Function } fetchMore - from GraphQL to fetch a new set of comment
		 *         { Boolean } isReactionsEnabled - a boolean value indicating whether reactions are enabled or not
		 * Comments array is divided to 3 part
		 * Part 1: the existing comment up to index of comment being edited or added
		 * Part 2: the newly added or edited comment
		 * Part 3: the existing comment after the index of comment being edited or added
		 */
		_onRefetchComments = (refetchOptions, fetchMore, isReactionsEnabled) => {
			const { contentId } = this.props;
			const { commentId } = refetchOptions;

			// COMMENTS-163 - Without querying the reactionsSummary resource we only need to use the
			//                commentId to query no longer need to pass in contentId as it's not used
			const variables = isReactionsEnabled
				? {
						commentId,
						contentId,
					}
				: {
						commentId,
					};
			const query = isReactionsEnabled
				? CommentsSectionGetSingleCommentQuery
				: CommentsSectionGetSingleCommentWithoutReactionsQuery;

			return fetchMore({
				variables,
				query,
				updateQuery: (prevData, { fetchMoreResult: fetchedResult }) => {
					const comment = fetchedResult?.comments?.nodes[0];
					if (!comment) {
						return prevData;
					}

					// FIXME: This should be a deep clone
					const newData = Object.assign({}, prevData);
					let newEdges = [];
					const { threadLastCommentId, parentId, mode } = refetchOptions;

					const prevDataEdges = prevData.comments.edges;

					const newDataEdges = newData.comments.edges;

					/*
            when edit comment, first find where the old comment is, then
            remove that old comment and add the new comment
          */
					/* istanbul ignore else */
					if (mode === 'editComment') {
						const insertIndex = prevDataEdges.findIndex((edge) => edge.node.id === commentId);

						const firstSegment = newDataEdges.slice(0, insertIndex);

						const secondSegment = [
							{
								cursor: insertIndex.toString(),
								node: comment,
								__typename: 'CommentEdge',
							},
						];
						const thirdSegment = newDataEdges.slice(insertIndex + 1);

						newEdges = [...firstSegment, ...secondSegment, ...thirdSegment];

						editedCommentsIds = [commentId];

						/*
              when reply to comment, first determine if the parent comment has
              any child comment. if "yes" append to the end of list of its child
              comment using `threadLastCommentId`. If "no" then just append after
              the parent comment
            */
					} else if (mode === 'replyComment') {
						const commentExistsInCache = newDataEdges.find((edge) => edge.node.id === commentId);
						if (commentExistsInCache) {
							return;
						}

						const showAfter = threadLastCommentId || parentId;

						const insertIndex = prevDataEdges.findIndex((edge) => edge.node.id === showAfter);

						const firstSegment = newDataEdges.slice(0, insertIndex + 1);
						const secondSegment = [
							{
								cursor: (insertIndex + 1).toString(),
								node: comment,
								__typename: 'CommentEdge',
							},
						];
						const thirdSegment = newDataEdges.slice(insertIndex + 1);

						newEdges = [...firstSegment, ...secondSegment, ...thirdSegment];

						/*
              reset so we no longer highlight edited comment after
              `replyComment`
            */
						editedCommentsIds = [];

						/*
              when add brand new comment to the page, just append to
              end of the array
            */
					} else if (mode === 'addComment' || parentId === '0') {
						const commentExistsInCache = newDataEdges.find((edge) => edge.node.id === commentId);
						if (commentExistsInCache) {
							return;
						}

						newEdges = newDataEdges.concat([
							{
								cursor: prevData.comments.totalCount.toString(),
								node: comment,
								__typename: 'CommentEdge',
							},
						]);

						/*
              reset so we no longer highlight edited comment after
              `addComment`
            */
						editedCommentsIds = [];
					}

					// new cache
					return Object.assign({}, prevData, {
						comments: {
							totalCount: newEdges.length,
							pageInfo: {
								endCursor: (newEdges.length - 1).toString(),
								hasNextPage: false, //set hasNextPage to always false to avoid performing incremental fetching again which will result in duplicate data
								__typename: newData.comments.pageInfo.__typename,
							},
							edges: newEdges,
							__typename: newData.comments.__typename,
						},
						content: {
							nodes: [...newData.content.nodes],
							__typename: newData.content.__typename,
						},
					});
				},
			});
		};

		render() {
			const {
				contentId,
				handlePageCommentsRender,
				isPageCommentsSortingExperimentEnabled,
				pageCommentsSortingExperimentCohort,
				isAutoReloadPageCommentsEnabled,
				inlineCommentsCount,
			} = this.props;

			if (!contentId) {
				return null;
			}

			/**
			 * For page with > 100 comment, we incrementally load comment in batch of 100 and save it to the cache using fetchMore
			 * As new batch of comment comes in, we append it to the end of array.
			 * Noted that GraphQL docs advises setting `notifyOnNetworkStatusChange = true`
			 * inside Query component to tell component to re-render whenever new set of data is loaded.
			 * This should not be done because of the current implementation and the fact that we are calling fetchMore inside the render function of Query component
			 * setting `notifyOnNetworkStatusChange = true` will cause unnecessary re-render and result in duplicate data being added to the cache
			 */

			return (
				<ReactionsProvider contentId={contentId}>
					<PageCommentsCountContext.Consumer>
						{({ setCount }) => (
							<ReactionsContext.Consumer>
								{({ isReactionsEnabled }) => {
									const query = isReactionsEnabled
										? CommentsSectionQuery
										: CommentsSectionWithoutReactionsQuery;

									return (
										<Query
											query={query}
											variables={{
												contentId,
												offset: '',
												first: DEFAULT_LIMIT,
											}}
											// preloading comment data on server-side, cache-first accomodates that
											// details at next/comments-section/src/preloadComments
											fetchPolicy="cache-first"
											errorPolicy="all"
										>
											{({ data, refetch, loading, error, fetchMore, networkStatus }) => (
												<Mutation mutation={CommentsSectionFabricQuerySaveComment}>
													{(experimentalFabricSaveComment, { data: saveCommentData }) => (
														<Mutation
															mutation={CommentsSectionQueryDeleteComment}
															/* eslint-disable react/jsx-no-bind */
															update={(
																cache,
																{
																	data: {
																		experimentalDeleteComment: { contentId: commentId },
																	},
																},
															) => {
																const prevData = cache.readQuery({
																	query,
																	variables: {
																		contentId,
																		offset: '',
																		first: DEFAULT_LIMIT,
																	},
																});

																const edges = prevData.comments.edges;

																const newEdges = edges
																	.filter((edge) => edge.node.id !== commentId)
																	.map((edge) => {
																		/**
																		 * update the ancestor array so the child comment of
																		 * the removed comment is outdented
																		 */
																		const newAncestors = edge.node.ancestors.filter(
																			(ancestorObj) => ancestorObj.id !== commentId,
																		);

																		const {
																			container,
																			body,
																			history,
																			id,
																			links,
																			operations,
																			version,
																		} = edge.node;

																		const oldData = {
																			container,
																			body,
																			history,
																			id,
																			links,
																			operations,
																			version,
																		};

																		const { cursor } = edge;

																		// new edge
																		return Object.assign({}, edge, {
																			cursor,
																			node: {
																				ancestors: newAncestors,
																				...oldData,
																				__typename: edge.node.__typename,
																			},
																			__typename: edge.__typename,
																		});
																	});

																const userData = prevData.user;

																setCount(newEdges.length);

																const newData = {
																	comments: {
																		totalCount: newEdges.length,
																		pageInfo: {
																			endCursor: '',
																			hasNextPage: false, //set hasNextPage to always false to avoid performing incremental fetching again which will result in duplicate data
																			__typename: prevData.comments.pageInfo.__typename,
																		},
																		edges: newEdges,
																		__typename: prevData.comments.__typename,
																	},
																	content: {
																		nodes: [...prevData.content.nodes],
																		__typename: prevData.content.__typename,
																	},
																	user: userData,
																};

																cache.writeQuery({
																	query,
																	variables: {
																		contentId,
																		offset: '',
																		first: DEFAULT_LIMIT,
																	},
																	data: {
																		...newData,
																	},
																});
															}}
														>
															{(
																experimentalDeleteComment,
																{ error: deleteCommentError, data: deleteCommentData },
															) => {
																if (error) {
																	// WS-3494 - If the error isn't related to reactionsSummary returning null, just mark it handled
																	// COMMENTS-188 - Or if the error isn't related to cc-pages not being reachable just mark it handled
																	if (
																		error.message.search(/reactions/i) !== -1 ||
																		error.message.search(/cc-pages/i) !== -1
																	) {
																		markErrorAsHandled(error);
																	} else {
																		// If we expect it, then mark it handled before showing the ErrorDisplay
																		if (isExpectedError(error) || isUnauthorizedError(error)) {
																			markErrorAsHandled(error);
																		}

																		// If we show the error display, hide the loading state from ViewPageFooterComments
																		handlePageCommentsRender && handlePageCommentsRender();

																		// Show error display to users unless it's related to reactions
																		return (
																			<ErrorDisplay error={error}>
																				<ViewCommentError
																					onLinkClick={() => this._refetchComments(refetch)}
																				/>
																			</ErrorDisplay>
																		);
																	}
																}

																if (
																	!data?.content?.nodes[0] ||
																	!data?.comments ||
																	!this._canShowCommentsWhenLoading(data, loading)
																) {
																	/*ViewPageFooterComments handles display of CommentPlaceholder
                              if handlePageCommentsRender is passed*/
																	if (handlePageCommentsRender) {
																		return null;
																	} else {
																		return <CommentPlaceholder />;
																	}
																}

																const pageData = data?.comments;

																const {
																	pageInfo: { hasNextPage, endCursor },
																} = pageData;

																const totalComments = pageData.totalCount;

																setCount(totalComments);

																const {
																	operations,
																	space: { homepageId, id: spaceId },
																	type: contentType,
																	subType,
																} = data?.content?.nodes[0];

																const currentUser = this._buildUserInfo(data?.user);

																const isSpaceHome = homepageId === contentId;

																const canAddComments = Boolean(
																	operations.find(
																		(op) =>
																			op.operation === 'create' && op.targetType === 'comment',
																	) &&
																		// we don't allow commenting on a space overview page, UNLESS it has comments already
																		Boolean(!isSpaceHome || totalComments > 0),
																);

																/**
																 * First fetch (offset = 0, first = 100) will be comment from index 0 - 99
																 * Second fetch (offset = 100, first = 100) will be from index 100 - 199 and so on until the end
																 */
																if (hasNextPage && !loading && !this._loadedCursor[endCursor]) {
																	this._loadedCursor[endCursor] = true;

																	this._fetchMore(fetchMore, {
																		contentId,
																		offset: endCursor,
																		first: DEFAULT_LIMIT,
																	});
																}

																/* eslint-disable react/jsx-no-bind */
																return (
																	<Fragment>
																		<CommentsSectionRedux
																			onRefetchComments={(refetchOptions) =>
																				this._onRefetchComments(
																					refetchOptions,
																					fetchMore,
																					isReactionsEnabled,
																				)
																			}
																			enableScrolling={this.enableScrolling.bind(this)}
																			isAutoScrollingEnabled={this.state.isAutoScrollingEnabled}
																			canAddComments={canAddComments}
																			currentUserInfo={currentUser}
																			canViewProfile={!!currentUser}
																			commentsData={this._buildCommentsData(data)}
																			contentId={contentId}
																			contentType={contentType}
																			contentSubType={subType}
																			spaceId={spaceId}
																			currentUserCanViewProfile={!!currentUser}
																			isAnonymous={!currentUser}
																			totalComments={totalComments}
																			hasFetchedComments={!loading}
																			hasNextPage={hasNextPage}
																			networkStatus={networkStatus}
																			saveComment={this._saveComment(
																				contentId,
																				contentType,
																				experimentalFabricSaveComment,
																			)}
																			deleteComment={this._deleteComment(
																				contentId,
																				experimentalDeleteComment,
																			)}
																			saveCommentData={saveCommentData}
																			deleteCommentData={deleteCommentData}
																			deleteCommentError={deleteCommentError}
																			editedCommentsIds={editedCommentsIds}
																			inlineCommentsCount={inlineCommentsCount}
																			handlePageCommentsRender={handlePageCommentsRender}
																			isPageCommentsSortingExperimentEnabled={
																				isPageCommentsSortingExperimentEnabled
																			}
																			pageCommentsSortingExperimentCohort={
																				pageCommentsSortingExperimentCohort
																			}
																		/>
																		{isAutoReloadPageCommentsEnabled ? (
																			<AutoReloadPageComments
																				contentId={contentId}
																				contentType={contentType}
																				lastPollTime={Date.now()}
																				reloader={() => this._refetchComments(refetch)}
																			/>
																		) : (
																			<QuickReloadSubscriber
																				name="commentsPage"
																				types={[ReloadType.content, ReloadType.comment]}
																				reload={() => {
																					this._refetchComments(refetch);
																				}}
																			/>
																		)}
																	</Fragment>
																);
																/* eslint-enable react/jsx-no-bind */
															}}
														</Mutation>
													)}
												</Mutation>
											)}
										</Query>
									);
								}}
							</ReactionsContext.Consumer>
						)}
					</PageCommentsCountContext.Consumer>
				</ReactionsProvider>
			);
		}
	},
);

export default CommentsSectionFabric;
